import { globalStyle, style } from '@vanilla-extract/css';

import columnsStyles from '../columnsElement/Columns.css';

const generateColumnOrderSelectorsGlobal = (totalColumns: number): void => {
  Array.from({ length: totalColumns }).forEach((_, i) => {
    globalStyle(`${columnsStyles.column} > div:nth-child(${i + 1})`, {
      '@media': {
        'only screen and (--mobile)': {
          order: `var(--mobile-column-order-${i + 1})`,
          display: `var(--mobile-column-display-${i + 1}, flex)`,
        },
      },
    });
  });
};

generateColumnOrderSelectorsGlobal(12);

export const baseContainer = style({
  position: 'var(--position)',
  top: 'var(--position-top)',
  right: 'var(--position-right)',
  bottom: 'var(--position-bottom)',
  transform: 'var(--position-transform)',
  left: 'var(--position-left)',
  zIndex: 'var(--z-index)',
  width: 'var(--width)',
  maxWidth: 'var(--max-width)',
  display: 'flex',
  gap: 'var(--gap)',
  flexDirection: 'var(--flex-direction)',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'var(--flex-wrap)',

  '@media': {
    'only screen and (--mobile)': {
      width: 'var(--mobile-width, var(--width))',
      maxWidth: 'var(--mobile-max-width, var(--max-width))',
      flexDirection: 'var(--mobile-flex-direction)',
      gap: 'var(--mobile-gap)',
      position: 'var(--mobile-position, var(--position))',
      transform: 'var(--mobile-position-transform)',
      top: 'var(--mobile-position-top, var(--position-top))',
      right: 'var(--mobile-position-right, var(--position-right))',
      bottom: 'var(--mobile-position-bottom, var(--position-bottom))',
      left: 'var(--mobile-position-left, var(--position-left))',
      zIndex: 'var(--mobile-z-index, var(--z-index))',
    },
  },
});

export const alignLeft = style({
  alignItems: 'flex-start',
});
export const alignCenter = style({
  alignItems: 'center',
});
export const alignRight = style({
  alignItems: 'flex-end',
});
export const alignTop = style({
  justifyContent: 'flex-start',
});
export const alignMiddle = style({
  justifyContent: 'center',
});
export const alignBottom = style({
  justifyContent: 'flex-end',
});
export const justifyContentSpaceBetween = style({
  justifyContent: 'space-between',
});
export const justifyContentSpaceAround = style({
  justifyContent: 'space-around',
});
export const justifyContentSpaceEvenly = style({
  justifyContent: 'space-evenly',
});

export const mobileHorizontalAlignLeft = style({
  '@media': {
    'only screen and (--mobile)': {
      alignItems: 'flex-start',
    },
  },
});
export const mobileHorizontalAlignCenter = style({
  '@media': {
    'only screen and (--mobile)': {
      alignItems: 'center',
    },
  },
});

export const mobileHorizontalAlignRight = style({
  '@media': {
    'only screen and (--mobile)': {
      alignItems: 'flex-end',
    },
  },
});

export const linkWrapper = style([
  baseContainer,
  {
    transition: 'all 0.3s',
    ':hover': {
      marginTop: -10,
      marginBottom: 10,
    },
  },
]);

export const link = style([
  baseContainer,
  {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    selectors: {
      // This is to make sure that the contents of the container are clickable
      // in editor. This is a workaround, components shouldn't really care where it is
      // rendered (editor or not)
      '[data-dream-editor="true"] &': {
        zIndex: -1,
      },
    },
  },
]);

export const containerElement = style([
  baseContainer,
  {
    minHeight: 'var(--min-height)',
    margin: 'var(--margin)',
    padding: 'var(--padding)',
    isolation: 'isolate',
    overflow: 'var(--overflow)',
    aspectRatio: 'var(--aspect-ratio)',
    borderRadius: 'var(--border-radius)',
    boxShadow: 'var(--box-shadow)',
    transition: 'var(--transition)',

    selectors: {
      '&.card': {
        backgroundColor: 'unset',
      },
      [`${columnsStyles.column} > .react-renderer > &, ${columnsStyles.column} > &`]: {
        height: 'inherit',
        minWidth: '0',
      },
      '&:hover': {
        boxShadow: 'var(--hover-shadow, none) !important',
        backgroundColor: 'var(--hover-background-color, transparent) !important',
        borderColor: 'var(--hover-border-color, transparent) !important',
        borderWidth: 'var(--hover-border-width, 0) !important',
        borderStyle: 'var(--hover-border-style, none) !important',
        borderRadius: 'var(--hover-border-radius, 0) !important',
        transform: 'scale(var(--hover-scale, 1)) var(--position-transform, none)',
      },
    },

    '@media': {
      'only screen and (--mobile)': {
        padding: 'var(--mobile-padding)',
        gap: 'var(--mobile-gap) !important',
        borderWidth: 'var(--mobile-border-width) !important',
        borderColor: 'var(--mobile-border-color) !important',
        borderStyle: 'var(--mobile-border-style) !important',
        borderRadius: 'var(--mobile-border-radius) !important',
        selectors: {
          '&:hover': {
            transform: 'scale(var(--hover-scale, 1)) var(--mobile-position-transform, none)',
            borderWidth: 'var(--mobile-border-width, 0) !important',
            borderColor: 'var(--mobile-border-color, transparent) !important',
            borderStyle: 'var(--mobile-border-style, none) !important',
            borderRadius: 'var(--mobile-border-radius, 0) !important',
          },
        },
      },
    },
  },
]);

export const backgroundVideo = style({
  position: 'absolute',
  zIndex: -1,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  aspectRatio: 'var(--aspect-ratio)',
  borderRadius: 'var(--border-radius)',
  overflow: 'clip',
});

export const backgroundImage = style({
  position: 'absolute',
  inset: 0,
  userSelect: 'none',
  borderRadius: 'var(--border-radius)',
  overflow: 'clip',
  zIndex: -1,

  selectors: {
    [`${containerElement}.card &`]: {
      borderRadius: 10,
      overflow: 'hidden',
    },
  },
});

export const flexDirectionColumn = style({});
export const flexDirectionRow = style({});
export const heightFill = style({
  selectors: {
    [`${flexDirectionColumn} &`]: {
      flex: 1,
    },
    [`${flexDirectionRow} &`]: {
      height: '100%',
    },
  },
});

globalStyle(`${backgroundImage} img`, {
  pointerEvents: 'none',
});

globalStyle(`${backgroundImage} ~ *`, {
  zIndex: 1,
});
